import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from '../../constants/ApiService';

const AIGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [messages, setMessages] = useState([]);
  const [relatedPage, setRelatedPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [messages, loading]);

  const isObject = (value) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  };

  const hasAiResponseShape = (value) => {
    if (!isObject(value)) return false;

    return (
      Object.prototype.hasOwnProperty.call(value, 'isSuccess') ||
      Object.prototype.hasOwnProperty.call(value, 'IsSuccess') ||
      Object.prototype.hasOwnProperty.call(value, 'status') ||
      Object.prototype.hasOwnProperty.call(value, 'Status') ||
      Object.prototype.hasOwnProperty.call(value, 'message') ||
      Object.prototype.hasOwnProperty.call(value, 'Message') ||
      Object.prototype.hasOwnProperty.call(value, 'data') ||
      Object.prototype.hasOwnProperty.call(value, 'Data') ||
      Object.prototype.hasOwnProperty.call(value, 'pageRoute') ||
      Object.prototype.hasOwnProperty.call(value, 'PageRoute') ||
      Object.prototype.hasOwnProperty.call(value, 'missingFields') ||
      Object.prototype.hasOwnProperty.call(value, 'MissingFields')
    );
  };

  const getApiResult = (response) => {
    // apiService can return either axios response OR already-unwrapped response.data.
    // This keeps UI only reading API response. No CRUD JSON or insert API call is done here.
    if (hasAiResponseShape(response?.data)) return response.data;
    if (hasAiResponseShape(response)) return response;
    if (response?.data !== undefined) return response.data;
    return response || {};
  };

  const isApiSuccess = (response, result = null) => {
    const r = result || getApiResult(response);

    return (
      r?.status === true ||
      r?.Status === true ||
      r?.isSuccess === true ||
      r?.IsSuccess === true
    );
  };

  const toAiText = (value) => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'string') return value;

    if (typeof value === 'object') {
      return (
        value.text ||
        value.Text ||
        value.answer ||
        value.Answer ||
        value.message ||
        value.Message ||
        value.data ||
        value.Data ||
        JSON.stringify(value, null, 2)
      );
    }

    return String(value);
  };

  const cleanRoute = (route) => {
    const value = String(route || '').trim();
    if (!value) return '';
    return value.replace(/[.,;)]$/, '');
  };

  const getMissingFields = (result) => {
    const missing =
      result?.missingFields ||
      result?.MissingFields ||
      result?.data?.missingFields ||
      result?.data?.MissingFields ||
      result?.Data?.missingFields ||
      result?.Data?.MissingFields ||
      [];

    if (Array.isArray(missing)) return missing.filter(Boolean);

    if (typeof missing === 'string') {
      return missing
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    }

    return [];
  };

  const getModelStateErrors = (result) => {
    const errors = result?.errors || result?.Errors;

    if (!isObject(errors)) return [];

    return Object.entries(errors).flatMap(([field, values]) => {
      if (Array.isArray(values)) {
        return values.map((msg) => `${field}: ${msg}`);
      }

      return [`${field}: ${values}`];
    });
  };

  const buildMissingFieldText = (missingFields) => {
    if (!missingFields?.length) return '';

    return `\n\nMissing required fields:\n${missingFields
      .map((field) => `- ${field}`)
      .join('\n')}`;
  };

  const getAiTextFromResult = (result, response = null) => {
    const r = getApiResult(result || response || {});

    const raw =
      r?.data ??
      r?.Data ??
      r?.message ??
      r?.Message ??
      response?.data?.data ??
      response?.data?.Data ??
      response?.data?.message ??
      response?.data?.Message ??
      '';

    let text = toAiText(raw);

    const missingFields = getMissingFields(r);
    const modelErrors = getModelStateErrors(r);

    if (missingFields.length > 0) {
      const missingText = buildMissingFieldText(missingFields);

      if (!text.toLowerCase().includes('missing required fields')) {
        text = `${text || 'Required fields are missing.'}${missingText}`;
      }
    }

    if (modelErrors.length > 0) {
      const errorText = `Validation errors:\n${modelErrors
        .map((x) => `- ${x}`)
        .join('\n')}`;

      text = text ? `${text}\n\n${errorText}` : errorText;
    }

    return text;
  };

  const getRelatedPageFromApiText = (text) => {
    const value = toAiText(text);

    const routeMatch =
      value.match(/PageRoute:\s*(\/[^\s]+)/i) ||
      value.match(/pageRoute:\s*(\/[^\s]+)/i) ||
      value.match(/CreateRoute:\s*(\/[^\s]+)/i) ||
      value.match(/ListPageRoute:\s*(\/[^\s]+)/i) ||
      value.match(/Route:\s*(\/[^\s]+)/i) ||
      value.match(/Create Page:\s*(\/[^\s]+)/i) ||
      value.match(/Search\/List Page:\s*(\/[^\s]+)/i) ||
      value.match(/Search Page:\s*(\/[^\s]+)/i) ||
      value.match(/List Page:\s*(\/[^\s]+)/i) ||
      value.match(/"pageRoute"\s*:\s*"(\/[^"]+)"/i) ||
      value.match(/"PageRoute"\s*:\s*"(\/[^"]+)"/i) ||
      value.match(/"createRoute"\s*:\s*"(\/[^"]+)"/i) ||
      value.match(/"CreateRoute"\s*:\s*"(\/[^"]+)"/i) ||
      value.match(/"route"\s*:\s*"(\/[^"]+)"/i) ||
      value.match(/"Route"\s*:\s*"(\/[^"]+)"/i);

    const titleMatch =
      value.match(/PageTitle:\s*([^\n\r]+)/i) ||
      value.match(/pageTitle:\s*([^\n\r]+)/i) ||
      value.match(/CreatePageTitle:\s*([^\n\r]+)/i) ||
      value.match(/Where to go:\s*([^\n\r]+)/i) ||
      value.match(/Page:\s*([^\n\r]+)/i) ||
      value.match(/"pageTitle"\s*:\s*"([^"]+)"/i) ||
      value.match(/"PageTitle"\s*:\s*"([^"]+)"/i) ||
      value.match(/"title"\s*:\s*"([^"]+)"/i) ||
      value.match(/"Title"\s*:\s*"([^"]+)"/i);

    const route = cleanRoute(routeMatch?.[1]);

    if (!route) return null;

    return {
      title: titleMatch?.[1]?.trim() || 'Page',
      route,
    };
  };

  const buildPageInfoFromAiResult = (result, fallbackText = '') => {
    const r = getApiResult(result || {});
    const data = r?.data || r?.Data || {};

    const route = cleanRoute(
      r?.pageRoute ||
        r?.PageRoute ||
        r?.createRoute ||
        r?.CreateRoute ||
        r?.listPageRoute ||
        r?.ListPageRoute ||
        data?.pageRoute ||
        data?.PageRoute ||
        data?.createRoute ||
        data?.CreateRoute ||
        data?.listPageRoute ||
        data?.ListPageRoute ||
        data?.route ||
        data?.Route ||
        '',
    );

    const title =
      r?.pageTitle ||
      r?.PageTitle ||
      r?.createPageTitle ||
      r?.CreatePageTitle ||
      r?.listPageTitle ||
      r?.ListPageTitle ||
      data?.pageTitle ||
      data?.PageTitle ||
      data?.createPageTitle ||
      data?.CreatePageTitle ||
      data?.listPageTitle ||
      data?.ListPageTitle ||
      data?.title ||
      data?.Title ||
      '';

    if (route) {
      return {
        title: title || 'Page',
        route,
      };
    }

    return getRelatedPageFromApiText(fallbackText);
  };

  const getToastMessage = (result, fallback) => {
    return (
      result?.message ||
      result?.Message ||
      result?.data?.message ||
      result?.data?.Message ||
      result?.Data?.message ||
      result?.Data?.Message ||
      fallback
    );
  };

  const addChatMessage = (sender, text, page = null) => {
    if (!String(text || '').trim()) return;

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        sender,
        text,
        page,
        time: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      },
    ]);
  };

  const setAiReply = (text, page = null) => {
    const finalText = String(text || '');
    setGeneratedText(finalText);
    addChatMessage('ai', finalText, page);
  };

  const renderClickableText = (text, isUser = false) => {
    const value = String(text || '');

    if (!value) return null;

    const parts = value.split(/(\/[a-zA-Z0-9/_-]+)/g);

    return parts.map((part, index) => {
      if (/^\/[a-zA-Z0-9/_-]+$/.test(part)) {
        return (
          <Link
            key={`${part}-${index}`}
            to={part}
            className={
              isUser
                ? 'text-white fw-bold text-decoration-underline'
                : 'text-primary fw-bold text-decoration-underline'
            }
            style={{ cursor: 'pointer' }}
          >
            {part}
          </Link>
        );
      }

      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
  };

  const handleGenerateWithText = async (inputText) => {
    const text = String(inputText || '').trim();

    if (!text) {
      toast.warning('Please enter your question.');
      return;
    }

    try {
      setLoading(true);
      setGeneratedText('');
      setRelatedPage(null);

      addChatMessage('user', text);

      // IMPORTANT: UI will call only this one API.
      // Prompt parsing, required-field checking, JSON payload building,
      // insert API calling and route/page decision must happen API-side.
      const response = await apiService.post('api/Ai/GenerateContent', {
        prompt: text,
        type: 'custom',
      });

      const result = getApiResult(response);
      const success = isApiSuccess(response, result);

      const answerText =
        getAiTextFromResult(result, response) ||
        getToastMessage(result, 'Request processed.');

      const pageInfo = buildPageInfoFromAiResult(result, answerText);

      setAiReply(answerText, pageInfo);
      setRelatedPage(pageInfo || null);

      if (success) {
        toast.success(
          getToastMessage(result, 'Request processed successfully.'),
        );
      } else {
        toast.error(getToastMessage(result, 'Request failed.'));
      }
    } catch (error) {
      console.error('AI Error:', error);

      const errorResult = getApiResult(
        error?.response?.data || error?.response || {},
      );

      const errorText =
        getAiTextFromResult(errorResult, { data: errorResult }) ||
        error?.message ||
        'Something went wrong.';

      const pageInfo = buildPageInfoFromAiResult(errorResult, errorText);

      setAiReply(errorText, pageInfo);
      setRelatedPage(pageInfo || null);

      toast.error(
        getToastMessage(
          errorResult,
          'Request failed. Please check required fields.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    await handleGenerateWithText(prompt);
  };

  const handlePromptKeyDown = async (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await handleGenerate();
    }
  };

  const handleClear = () => {
    setPrompt('');
    setGeneratedText('');
    setMessages([]);
    setRelatedPage(null);
  };

  const getSpeechRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return null;

    return new SpeechRecognition();
  };

  const startVoiceSearch = () => {
    const recognition = getSpeechRecognition();

    if (!recognition) {
      toast.error(
        'Voice search is not supported in this browser. Please use Google Chrome.',
      );
      return;
    }

    recognition.lang = 'en-IN';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognitionRef.current = recognition;

    let finalTranscript = '';

    recognition.onstart = () => {
      setIsListening(true);
      setGeneratedText('');
      toast.info('Listening... please speak now.');
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const spokenText = `${finalTranscript} ${interimTranscript}`.trim();

      if (spokenText) {
        setPrompt(spokenText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);

      if (event.error === 'not-allowed') {
        toast.error(
          'Microphone permission denied. Please allow microphone access.',
        );
      } else if (event.error === 'no-speech') {
        toast.warning('No speech detected. Please try again.');
      } else {
        toast.error(`Voice error: ${event.error}`);
      }
    };

    recognition.onend = async () => {
      setIsListening(false);

      const spokenText = String(finalTranscript || prompt || '').trim();

      if (spokenText) {
        await handleGenerateWithText(spokenText);
      }
    };

    recognition.start();
  };

  const stopVoiceSearch = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setIsListening(false);
  };

  return (
    <div
      className='container-fluid py-4'
      style={{
        background: '#f6f8fb',
        minHeight: 'calc(100vh - 80px)',
      }}
    >
      <div className='row justify-content-center'>
        <div className='col-12 col-xl-11'>
          <div
            className='card border-0 shadow-sm'
            style={{
              borderRadius: '18px',
              overflow: 'hidden',
            }}
          >
            <div
              className='card-header border-0 text-white'
              style={{
                background: 'linear-gradient(135deg, #0d6efd 0%, #173b75 100%)',
                padding: '22px 26px',
              }}
            >
              <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
                <div className='d-flex align-items-center gap-3'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '15px',
                      background: 'rgba(255,255,255,0.18)',
                      fontSize: '23px',
                    }}
                  >
                    ✨
                  </div>

                  <div>
                    <h4 className='mb-1 fw-bold'>AI Assistant</h4>
                    <small style={{ opacity: 0.9 }}>
                      Ask anything using text or voice
                    </small>
                  </div>
                </div>

                <span
                  className='badge rounded-pill'
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    padding: '10px 14px',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  Smart Assistant
                </span>
              </div>
            </div>

            <div className='card-body p-4'>
              <div className='row g-4'>
                <div className='col-12 col-lg-12'>
                  <div
                    className='card border-0 h-100'
                    style={{
                      borderRadius: '16px',
                      background: '#ffffff',
                      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className='d-flex align-items-center justify-content-between flex-wrap gap-2'
                      style={{
                        padding: '18px 20px',
                        borderBottom: '1px solid #eef2f7',
                        background: '#ffffff',
                      }}
                    >
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='d-flex align-items-center justify-content-center'
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '14px',
                            background: '#eef5ff',
                            color: '#0d6efd',
                            fontSize: '18px',
                          }}
                        >
                          💬
                        </div>

                        <div>
                          <h6 className='fw-bold mb-0'>Conversation</h6>
                          <small className='text-muted'>
                            Ask naturally and get AI replies here
                          </small>
                        </div>
                      </div>

                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                        {relatedPage?.route && (
                          <Link
                            to={relatedPage.route}
                            className='btn btn-sm btn-outline-primary'
                            style={{
                              borderRadius: '10px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Open {relatedPage.title || 'Page'}
                          </Link>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        minHeight: '390px',
                        maxHeight: '480px',
                        overflowY: 'auto',
                        background:
                          'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                        padding: '20px',
                      }}
                    >
                      {messages.length === 0 && !loading && (
                        <div
                          className='d-flex flex-column align-items-center justify-content-center text-center'
                          style={{
                            minHeight: '340px',
                            color: '#64748b',
                          }}
                        >
                          <div
                            className='d-flex align-items-center justify-content-center mb-3'
                            style={{
                              width: '68px',
                              height: '68px',
                              borderRadius: '22px',
                              background: '#ffffff',
                              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                              fontSize: '30px',
                            }}
                          >
                            ✨
                          </div>

                          <h6 className='fw-bold text-dark mb-1'>
                            Start a conversation
                          </h6>
                          <div
                            style={{
                              maxWidth: '520px',
                              lineHeight: '1.6',
                              fontSize: '14px',
                            }}
                          >
                            Type your question, CRUD save command, page search,
                            or use voice search. Your conversation will appear
                            here.
                          </div>
                        </div>
                      )}

                      {messages.map((msg) => {
                        const isUser = msg.sender === 'user';

                        return (
                          <div
                            key={msg.id}
                            className={`d-flex mb-3 ${
                              isUser
                                ? 'justify-content-end'
                                : 'justify-content-start'
                            }`}
                          >
                            {!isUser && (
                              <div
                                className='me-2 d-none d-sm-flex align-items-center justify-content-center'
                                style={{
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '50%',
                                  background: '#ffffff',
                                  border: '1px solid #e5e7eb',
                                  boxShadow:
                                    '0 4px 10px rgba(15, 23, 42, 0.06)',
                                  flexShrink: 0,
                                }}
                              >
                                🤖
                              </div>
                            )}

                            <div
                              style={{
                                maxWidth: '78%',
                                minWidth: '120px',
                              }}
                            >
                              <div
                                style={{
                                  borderRadius: isUser
                                    ? '18px 18px 4px 18px'
                                    : '18px 18px 18px 4px',
                                  padding: '13px 16px',
                                  background: isUser ? '#0d6efd' : '#ffffff',
                                  color: isUser ? '#ffffff' : '#111827',
                                  border: isUser ? '0' : '1px solid #e5e7eb',
                                  boxShadow:
                                    '0 6px 16px rgba(15, 23, 42, 0.08)',
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: '1.65',
                                  fontSize: '15px',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <div
                                  className='fw-semibold mb-1'
                                  style={{
                                    fontSize: '12px',
                                    opacity: isUser ? 0.9 : 0.65,
                                  }}
                                >
                                  {isUser ? 'You' : 'AI Assistant'}
                                </div>

                                <div>
                                  {renderClickableText(msg.text, isUser)}
                                </div>

                                {msg.page?.route && (
                                  <div
                                    className='mt-3 pt-2'
                                    style={{
                                      borderTop: isUser
                                        ? '1px solid rgba(255,255,255,0.35)'
                                        : '1px solid #e5e7eb',
                                    }}
                                  >
                                    <div className='mb-1 fw-semibold'>
                                      Related Page
                                    </div>

                                    <Link
                                      to={msg.page.route}
                                      className={
                                        isUser
                                          ? 'text-white fw-bold text-decoration-underline'
                                          : 'text-primary fw-bold text-decoration-underline'
                                      }
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {msg.page.title || 'Open Page'} (
                                      {msg.page.route})
                                    </Link>
                                  </div>
                                )}
                              </div>

                              <div
                                className={`mt-1 small ${
                                  isUser ? 'text-end' : 'text-start'
                                }`}
                                style={{
                                  color: '#94a3b8',
                                  fontSize: '11px',
                                }}
                              >
                                {msg.time}
                              </div>
                            </div>

                            {isUser && (
                              <div
                                className='ms-2 d-none d-sm-flex align-items-center justify-content-center'
                                style={{
                                  width: '34px',
                                  height: '34px',
                                  borderRadius: '50%',
                                  background: '#0d6efd',
                                  color: '#ffffff',
                                  boxShadow:
                                    '0 4px 10px rgba(13, 110, 253, 0.25)',
                                  flexShrink: 0,
                                }}
                              >
                                👤
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {loading && (
                        <div className='d-flex justify-content-start mb-3'>
                          <div
                            className='me-2 d-none d-sm-flex align-items-center justify-content-center'
                            style={{
                              width: '34px',
                              height: '34px',
                              borderRadius: '50%',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 4px 10px rgba(15, 23, 42, 0.06)',
                              flexShrink: 0,
                            }}
                          >
                            🤖
                          </div>

                          <div
                            style={{
                              borderRadius: '18px 18px 18px 4px',
                              padding: '13px 16px',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
                              color: '#64748b',
                              fontSize: '15px',
                            }}
                          >
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                              aria-hidden='true'
                            />
                            AI is typing...
                          </div>
                        </div>
                      )}

                      <div ref={chatEndRef} />
                    </div>

                    <div
                      style={{
                        padding: '18px 20px 20px',
                        background: '#ffffff',
                        borderTop: '1px solid #eef2f7',
                      }}
                    >
                      <div className='position-relative'>
                        <textarea
                          className='form-control border-0'
                          rows='3'
                          placeholder='Type your message here...'
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          onKeyDown={handlePromptKeyDown}
                          disabled={loading || isListening}
                          style={{
                            resize: 'vertical',
                            minHeight: '100px',
                            borderRadius: '18px',
                            background: '#f8fafc',
                            padding: '16px',
                            paddingRight: '150px',
                            paddingBottom: '58px',
                            fontSize: '15px',
                            lineHeight: '1.6',
                            boxShadow: 'inset 0 0 0 1px #e5e7eb',
                          }}
                        />

                        <div
                          className='position-absolute d-flex align-items-center gap-2'
                          style={{
                            right: '14px',
                            bottom: '14px',
                            zIndex: 2,
                          }}
                        >
                          {prompt?.trim() && (
                            <span
                              className='text-muted small d-none d-sm-inline'
                              style={{
                                background: '#f8fafc',
                                padding: '4px 6px',
                                borderRadius: '8px',
                              }}
                            >
                              {prompt.length} chars
                            </span>
                          )}

                          <button
                            type='button'
                            className={
                              isListening
                                ? 'btn btn-danger d-flex align-items-center justify-content-center'
                                : 'btn btn-light border d-flex align-items-center justify-content-center'
                            }
                            onClick={
                              isListening ? stopVoiceSearch : startVoiceSearch
                            }
                            disabled={loading}
                            title={
                              isListening ? 'Stop Listening' : 'Voice Search'
                            }
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              fontSize: '18px',
                              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                            }}
                          >
                            {isListening ? '■' : '🎤'}
                          </button>

                          <button
                            type='button'
                            className='btn btn-primary d-flex align-items-center justify-content-center'
                            onClick={handleGenerate}
                            disabled={loading || isListening || !prompt.trim()}
                            title='Send'
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              fontSize: '18px',
                              fontWeight: 700,
                              boxShadow: '0 6px 16px rgba(13, 110, 253, 0.28)',
                            }}
                          >
                            {loading ? (
                              <span
                                className='spinner-border spinner-border-sm'
                                role='status'
                                aria-hidden='true'
                              />
                            ) : (
                              '➤'
                            )}
                          </button>
                        </div>
                      </div>

                      <div className='d-flex justify-content-between align-items-center mt-2 px-1'>
                        {messages.length > 0 && (
                          <button
                            type='button'
                            className='btn btn-link text-secondary text-decoration-none p-0'
                            onClick={handleClear}
                            disabled={loading || isListening}
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                            }}
                          >
                            Clear chat
                          </button>
                        )}

                        {generatedText && (
                          <span
                            className='small text-muted ms-auto'
                            style={{ fontSize: '12px' }}
                          >
                            Last response generated
                          </span>
                        )}
                      </div>

                      {isListening && (
                        <div
                          className='alert alert-info d-flex align-items-center gap-2 mt-3 mb-0'
                          style={{
                            borderRadius: '12px',
                            border: '0',
                          }}
                        >
                          <span style={{ fontSize: '20px' }}>🎤</span>
                          <div>
                            <strong>Listening...</strong> Speak now.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIGenerator;
//22 new
// import React, { useEffect, useRef, useState } from "react";
// import { toast } from "react-toastify";
// import { apiService } from "../../constants/ApiService";

// const AIGenerator = () => {
//   const [prompt, setPrompt] = useState("");
//   const [messages, setMessages] = useState([]);
//   const chatEndRef = useRef(null);
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [messages]);

//   const addChatMessage = (sender, text) => {
//     if (!text) return;
//     setMessages((prev) => [
//       ...prev,
//       { id: Date.now(), sender, text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
//     ]);
//   };

//   const setAiReply = (text) => addChatMessage("ai", text);

//   const fetchRequiredFieldsFromApi = async (entityType) => {
//     try {
//       const res = await apiService.get(`/api/Ai/GetModuleRequiredFields?entityType=${entityType}`);
//       return res.data?.requiredFields || [];
//     } catch {
//       return [];
//     }
//   };

//   const handleCreateEntity = async (entityType, formData) => {
//     const requiredFields = await fetchRequiredFieldsFromApi(entityType);
//     for (const field of requiredFields) {
//       if (!formData[field] || formData[field].toString().trim() === "") {
//         alert(`${field} is required`);
//         return;
//       }
//     }

//     const payload = { EntityType: entityType, Data: formData, RequiredFields: requiredFields };
//     setLoading(true);
//     try {
//       const response = await apiService.post("/api/Ai/CreateEntityFromAi", payload);
//       if (response.data?.status) {
//         setAiReply(`${entityType} created successfully`);
//         toast.success(`${entityType} created successfully`);
//       } else {
//         setAiReply(response.data?.message || `Failed to create ${entityType}`);
//         toast.error(response.data?.message || `Failed to create ${entityType}`);
//       }
//     } catch (err) {
//       setAiReply(`Error creating ${entityType}: ${err.message}`);
//       toast.error(`Error creating ${entityType}: ${err.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSend = async () => {
//     if (!prompt.trim()) return;
//     addChatMessage("user", prompt);

//     const text = prompt.toLowerCase();
//     if (text.includes("lead")) {
//       await handleCreateEntity("lead", { Name: "John Doe", Email: "john@example.com", Mobile: "9876543210" });
//     } else if (text.includes("subject")) {
//       await handleCreateEntity("subject", { subject_name: "Math", subject_code: "M101", subject_marks: 100, class_name: "10th Grade" });
//     } else if (text.includes("faculty")) {
//       await handleCreateEntity("faculty", { faculty_name: "Rahul Sharma", faculty_code: "F101", email: "rahul@gmail.com", mobile: "9876543210" });
//     }

//     setPrompt("");
//   };

//   const handleClear = () => {
//     setPrompt("");
//     setMessages([]);
//   };

//   return (
//     <div style={{ padding: "20px", background: "#f0f2f5", minHeight: "100vh", maxWidth: "700px", margin: "0 auto" }}>
//       {/* Chat container */}
//       <div style={{ maxHeight: "500px", overflowY: "auto", marginBottom: "10px", padding: "10px" }}>
//         {messages.map((msg) => (
//           <div
//             key={msg.id}
//             style={{
//               display: "flex",
//               justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
//               marginBottom: "10px",
//             }}
//           >
//             <div
//               style={{
//                 maxWidth: "70%",
//                 padding: "10px 15px",
//                 borderRadius: "20px",
//                 backgroundColor: msg.sender === "user" ? "#007bff" : "#e4e6eb",
//                 color: msg.sender === "user" ? "#fff" : "#000",
//                 wordBreak: "break-word",
//               }}
//             >
//               {msg.text}
//               <div style={{ fontSize: "0.7em", textAlign: "right", marginTop: "2px" }}>{msg.time}</div>
//             </div>
//           </div>
//         ))}
//         <div ref={chatEndRef} />
//       </div>

//       {/* Input area */}
//       <textarea
//         value={prompt}
//         onChange={(e) => setPrompt(e.target.value)}
//         placeholder="Ask AI to create Lead, Subject, or Faculty..."
//         rows={3}
//         style={{ width: "100%", marginBottom: "10px", padding: "10px", borderRadius: "5px" }}
//       />

//       <div style={{ display: "flex", justifyContent: "space-between" }}>
//         <button
//           onClick={handleSend}
//           disabled={loading}
//           style={{ padding: "10px 20px", background: "#007bff", color: "#fff", border: "none", cursor: "pointer", borderRadius: "5px" }}
//         >
//           {loading ? "Processing..." : "Send"}
//         </button>
//         {messages.length > 0 && (
//           <button
//             onClick={handleClear}
//             style={{ padding: "10px 20px", background: "#6c757d", color: "#fff", border: "none", cursor: "pointer", borderRadius: "5px" }}
//           >
//             Clear Chat
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// export default AIGenerator;
